// Payouts → Agreements & gates — the commercial terms, readable first.
//
// Rule 4 of the design: every rate lives in a dated, versioned row, never in
// code. This screen leads with WHAT THE TERMS SAY — the waterfall as one
// labelled bar, each role's terms in words ("pays only days with ≥4 trips
// and ≥5 driving-hours") — and only then offers the machinery to change
// them. Changes are forward-only at an IST month boundary (server-enforced;
// the form only offers months), because the partner slab is a monthly
// quantity and a mid-month change would make the blended rate ambiguous.
//
// The gate and the SIM recovery are FLEET terms: a fleet gets its own by
// getting its own premise-operator row, which overrides the default for its
// devices from the chosen month. The form builds every term from typed
// fields — an operator should never hand-write JSON to change a number.

import React, { useMemo, useState } from 'react';
import { PageHead, Loading, ErrorState, Pill, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { inr, pct, monthLabel, ROLE_LABEL, BASE, shaped } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

const nextMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function gateWords(g) {
  if (!g) return null;
  const parts = [];
  if (g.min_trips != null) parts.push(`≥ ${g.min_trips} trips`);
  if (g.min_driving_hours != null) parts.push(`≥ ${g.min_driving_hours} driving-hours`);
  if (g.min_presence_hours != null) parts.push(`≥ ${g.min_presence_hours} passenger-hours`);
  return parts.length ? `Pays only days with ${parts.join(' and ')}` : 'No day gate';
}

function simWords(x) {
  if (!x || !x.sim_per_device_month_paise) return null;
  return `Recovers ${inr(x.sim_per_device_month_paise, 0)} per device per month — ungated, deducted before TDS`;
}

// ── the waterfall, as one labelled bar ──────────────────────────────────────
function WaterfallBar({ defaults }) {
  const by = (role) => defaults.find((a) => a.role === role);
  const tech = by('platform_tech');
  const premise = by('premise_operator');
  const device = by('device_owner');
  const margin = by('platform_margin');
  if (!tech || !premise || !device || !margin) return null;
  const techPct = tech.rate_bps / 100;
  const poolShare = (a) => ((10000 - tech.rate_bps) / 10000) * (a.rate_bps / 10000) * 100;
  const per1000 = (p) => inr(Math.round(p * 1000), 0);
  const segs = [
    { cls: s.wfTech, label: 'Technical fees', pctOfGross: techPct },
    { cls: s.wfPremise, label: 'Premise operator', pctOfGross: poolShare(premise) },
    { cls: s.wfDevice, label: 'Device owner', pctOfGross: poolShare(device) },
    { cls: s.wfMargin, label: 'pDOOH', pctOfGross: poolShare(margin) },
  ];
  return (
    <div className={s.wfWrap}>
      <div className={s.wfBar}>
        {segs.map((seg) => (
          <div key={seg.label} className={`${s.wfSeg} ${seg.cls}`} style={{ width: `${seg.pctOfGross}%` }}
            title={`${seg.label}: ${seg.pctOfGross.toFixed(1)}% of gross = ${per1000(seg.pctOfGross)} per ₹1,000 settled`}>
            <span className="segLabel">{seg.label}</span>
            <span className="segValue">{per1000(seg.pctOfGross)}</span>
          </div>
        ))}
      </div>
      <div className={s.wfNote}>
        Per ₹1,000 of settled advertiser spend. A partner/agency’s staged commission (8→15% of the brand’s
        monthly book, marginal like income-tax slabs) is paid <b>out of pDOOH’s share</b> at month close —
        the fleet shares never move when a partner exists.
      </div>
    </div>
  );
}

// ── one role's current terms, in words ──────────────────────────────────────
function RoleCard({ agreement, defaults }) {
  const a = agreement;
  const isDefault = a.party_tenant_id == null;
  const techBps = defaults.find((d) => d.role === 'platform_tech')?.rate_bps ?? 0;
  const ofGross = a.basis === 'gross' ? a.rate_bps / 100
    : ((10000 - techBps) / 10000) * (a.rate_bps / 100);
  return (
    <div className={s.roleCard}>
      <span className={s.roleName}>
        {ROLE_LABEL[a.role]}
        {!isDefault && <Pill tone="iris">{a.party_name}</Pill>}
      </span>
      <span className={s.roleRate}>
        {pct(a.rate_bps)} <small style={{ whiteSpace: 'nowrap' }}>{a.basis === 'gross' ? 'of gross' : 'of the pool'}</small>
        {a.basis !== 'gross' && <small> — {ofGross.toFixed(0)}% of gross</small>}
      </span>
      {gateWords(a.gate_profile) && <span className={s.roleTerm}><b>{gateWords(a.gate_profile)}</b> — judged fresh per device, per IST day.</span>}
      {simWords(a.expense_config) && <span className={s.roleTerm}>{simWords(a.expense_config)}.</span>}
      {a.rate_slabs && (
        <span className={s.roleTerm}>
          Funds the partner commission, staged on the brand’s monthly book:
          {a.rate_slabs.map((sl, i) => {
            const lo = i === 0 ? 0 : a.rate_slabs[i - 1].up_to_paise;
            return (
              <span className={s.slabRow} key={i}>
                <span>{sl.up_to_paise == null ? `beyond ${inr(lo, 0)}` : `${inr(lo, 0)} – ${inr(sl.up_to_paise, 0)}`}</span>
                <b>{pct(sl.rate_bps)}</b>
              </span>
            );
          })}
        </span>
      )}
      <span className={s.roleTerm}>
        {['premise_operator', 'device_owner', 'partner_commission'].includes(a.role) && (
          <>TDS: {a.tds_bps == null
            ? <Pill tone="warn">not set — blocks the first confirm</Pill>
            : <b>{pct(a.tds_bps)}</b>}{' · '}</>
        )}
        effective {monthLabel(a.effective_from)} · v{a.version}
      </span>
    </div>
  );
}

// ── the change form: typed fields build the terms; no JSON in sight ─────────
function NewVersionForm({ tenants, onDone }) {
  const [role, setRole] = useState('premise_operator');
  const [party, setParty] = useState('');
  const [ratePct, setRatePct] = useState('');
  const [month, setMonth] = useState(nextMonthISO());
  const [tdsPct, setTdsPct] = useState('');
  // founder decree 8 Sep 2026: the default gate becomes 4 trips + 4
  // passenger-hours (TOF); driving-hours drops out of the default. The
  // GOVERNING gate is always the agreement ROW — these are form seeds.
  const [gTrips, setGTrips] = useState('4');
  const [gDrive, setGDrive] = useState('');
  const [gPax, setGPax] = useState('4');
  const [simRs, setSimRs] = useState('170');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);

  const isPremise = role === 'premise_operator';
  const basis = role === 'platform_tech' ? 'gross' : 'net_pool';

  const submit = async () => {
    setResult(null);
    try {
      const body = {
        role,
        party_tenant_id: party || null,
        basis,
        rate_bps: Math.round(parseFloat(ratePct) * 100),
        effective_from: `${month}-01`,
        tds_bps: tdsPct.trim() === '' ? null : Math.round(parseFloat(tdsPct) * 100),
        notes: notes || null,
      };
      if (isPremise) {
        const gate = {};
        if (gTrips.trim() !== '') gate.min_trips = Number(gTrips);
        if (gDrive.trim() !== '') gate.min_driving_hours = Number(gDrive);
        if (gPax.trim() !== '') gate.min_presence_hours = Number(gPax);
        body.gate_profile = Object.keys(gate).length ? { kind: 'in_cab', ...gate } : null;
        body.expense_config = simRs.trim() !== ''
          ? { sim_per_device_month_paise: Math.round(parseFloat(simRs) * 100), basis: 'paired_in_month' }
          : null;
      }
      const r = await api.post(`${BASE}/agreements`, body);
      setResult({ ok: true, text: `Version ${r.data.version} created, effective ${monthLabel(`${month}-01`)}. History stands; nothing recomputes behind it.` });
      onDone();
    } catch (e) {
      setResult({ ok: false, text: e?.response?.data?.details || e?.response?.data?.error || e.message });
    }
  };

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>Change terms — a new version, from a month boundary, forward only</div>
      <div className={s.panelSub}>
        Pick who and what; the change takes effect at the chosen month and never touches history.
        Leave “Fleet” on the default to change the system-wide terms; pick a fleet to give them their own.
      </div>
      <div className={s.formRow}>
        <div className={s.field}>
          <label>Term</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="premise_operator">Premise operator (rate · gate · SIM)</option>
            <option value="device_owner">Device owner (rate)</option>
            <option value="platform_tech">Technical fees (rate)</option>
            <option value="platform_margin">pDOOH margin (rate)</option>
          </select>
        </div>
        <div className={`${s.field} ${s.fieldWide}`}>
          <label>Fleet / party</label>
          <select value={party} onChange={(e) => setParty(e.target.value)}>
            <option value="">System default — every fleet without its own terms</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className={s.field}>
          <label>Rate ({basis === 'gross' ? '% of gross' : '% of pool'})</label>
          <input type="number" step="0.1" value={ratePct} onChange={(e) => setRatePct(e.target.value)} placeholder={isPremise ? '30' : ''} />
        </div>
        <div className={s.field}>
          <label>Effective from</label>
          <input type="month" min={nextMonthISO()} value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div className={s.field}>
          <label>TDS %</label>
          <input type="number" step="0.1" value={tdsPct} onChange={(e) => setTdsPct(e.target.value)} placeholder="2 (194C) / 5 (194H)" />
        </div>
      </div>
      {isPremise && (
        <div className={s.formRow}>
          <div className={s.field}>
            <label>Gate · min trips / day</label>
            <input type="number" value={gTrips} onChange={(e) => setGTrips(e.target.value)} />
          </div>
          <div className={s.field}>
            <label>Gate · min driving hours</label>
            <input type="number" step="0.5" value={gDrive} onChange={(e) => setGDrive(e.target.value)} />
          </div>
          <div className={s.field}>
            <label>Gate · min passenger hours</label>
            <input type="number" step="0.5" value={gPax} onChange={(e) => setGPax(e.target.value)} placeholder="blank = none" />
          </div>
          <div className={s.field}>
            <label>SIM ₹ / device / month</label>
            <input type="number" value={simRs} onChange={(e) => setSimRs(e.target.value)} />
          </div>
        </div>
      )}
      <div className={s.formRow}>
        <div className={`${s.field} ${s.fieldWide}`}>
          <label>Why (prints in the history)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. CabE pilot terms per the 3 Sep call" />
        </div>
        <Button primary onClick={submit} disabled={!ratePct || !month}>Create version</Button>
      </div>
      <div className={s.hint}>
        Try a gate before committing it: the dial on the Eligibility screen replays any candidate over the
        frozen history and shows what it would have paid.
      </div>
      {result && <div className={result.ok ? s.okBanner : s.badBanner}>{result.text}</div>}
    </div>
  );
}

export default function Agreements() {
  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/agreements`).then((r) => shaped(r.data, 'agreements')), []);
  const subjects = useApi(() => api.get(`${BASE}/subjects`).then((r) => shaped(r.data, 'tenants')), []);

  const rows = data?.agreements || [];
  const { current, history, overrides } = useMemo(() => {
    const cur = rows.filter((a) => a.effective_to == null && a.party_tenant_id == null);
    const ov = rows.filter((a) => a.effective_to == null && a.party_tenant_id != null);
    const hist = rows.filter((a) => a.effective_to != null);
    return { current: cur, history: hist, overrides: ov };
  }, [rows]);

  const head = (
    <PageHead
      title="Agreements & gates"
      sub="The commercial terms as dated, versioned rows — the code carries the arithmetic, these rows carry every number. Changes are forward-only from a month boundary."
    />
  );

  if (loading) return <div className={s.wrap}>{head}<Loading what="the agreements" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reload} /></div>;

  return (
    <div className={s.wrap}>
      {head}

      <WaterfallBar defaults={current} />

      <div className={s.roleGrid}>
        {['premise_operator', 'device_owner', 'platform_margin', 'platform_tech'].map((role) => {
          const a = current.find((x) => x.role === role);
          return a ? <RoleCard key={a.id} agreement={a} defaults={current} /> : null;
        })}
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>Fleet-specific terms</div>
        {overrides.length === 0 ? (
          <div className={s.panelSub} style={{ margin: 0 }}>
            None yet — every fleet runs the system defaults above. Give a fleet its own gate, SIM rate
            or revenue split with the form below; it overrides the default for their devices from the
            chosen month.
          </div>
        ) : (
          <div className={s.roleGrid} style={{ marginBottom: 0 }}>
            {overrides.map((a) => <RoleCard key={a.id} agreement={a} defaults={current} />)}
          </div>
        )}
      </div>

      <NewVersionForm tenants={subjects.data?.tenants || []} onDone={reload} />

      <Explainer terms={['waterfall', 'premise', 'deviceOwner', 'gate', 'sim', 'tds', 'house']} />

      {history.length > 0 && (
        <details className={s.panel}>
          <summary className={s.panelTitle} style={{ cursor: 'pointer' }}>
            Superseded versions ({history.length}) — history never recomputes
          </summary>
          {history.map((a) => (
            <div key={a.id} className={s.subjectRow}>
              <span className={s.subjectName}>{ROLE_LABEL[a.role]}</span>
              <span className={s.ownerChip}>{a.party_name || 'system default'}</span>
              <span className={s.ownerChip}><b>{pct(a.rate_bps)}</b> {a.basis}</span>
              <span className={s.ownerChip}>{monthLabel(a.effective_from)} → {monthLabel(a.effective_to)}</span>
              <span className={s.ownerChip}>v{a.version}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
